import { Link, Stack, Typography, useTheme } from "@mui/material";
import _ from "lodash";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionSkeleton,
  AccordionTitle,
  SnackbarContent,
  TaskItem,
  TaskItemsTable,
  useDrawer,
  ViewList,
} from "src/components";
import { Links, TASK_ITEM_STATUSES, UserTaskItemsGroupByOption } from "src/constants";
import { useSession } from "src/hooks";
import { TaskItemDto, useGetUserTaskItems, UserDto } from "src/http";
import { useUserImplementationSettings } from "../hooks";

interface Props {
  userGuid: string;
  isTableView: boolean;
}

const UserTaskItemsList = (props: Props) => {
  const { displayName: sessionUserDisplayName, userId: sessionUserGuid } = useSession();
  const { settings } = useUserImplementationSettings(props.userGuid);
  const { data, isLoading } = useGetUserTaskItems(props.userGuid, {
    filters: {
      status: settings.taskItemsFilterByStatusOptions?.map((x) => x.toString()) ?? null,
    },
    sortProperty: settings.taskItemsSortByOption,
    sortDirection: settings.taskItemsSortByDirection,
  });
  const { closeDrawer } = useDrawer();
  const [groupedTasks, setGroupedTasks] = useState<Map<string, TaskItemDto[]> | undefined>(undefined);
  const theme = useTheme();

  const groupByFunction = useCallback(
    (item: TaskItemDto) => {
      switch (settings.taskItemsGroupByOption) {
        case UserTaskItemsGroupByOption.None: {
          return "All Tasks";
        }
        case UserTaskItemsGroupByOption.DueDate: {
          if (item.dueDate == null) {
            return "No Due Date";
          }

          const dueDate = DateTime.fromISO(item.dueDate);

          if (dueDate < DateTime.now()) {
            return "Past Due";
          } else if (dueDate < DateTime.now().plus({ week: 1 })) {
            return "Due Soon";
          } else {
            return "Due Later";
          }
        }
        case UserTaskItemsGroupByOption.InFocus: {
          return item.owners.some((owner) => owner.user.userId === props.userGuid && owner.isFocused)
            ? "Tasks in Focus"
            : "All Other Tasks";
        }
        case UserTaskItemsGroupByOption.Owner: {
          if (item.owners.length === 0) {
            return "No Owner";
          }

          return item.owners
            .map((x) => x.user.displayName)
            .sort((a, b) => a.localeCompare(b))
            .join(", ");
        }
        case UserTaskItemsGroupByOption.PrimaryOwner: {
          if (item.primaryOwner == null) {
            return "No Primary Owner";
          }

          return item.primaryOwner.displayName;
        }
        case UserTaskItemsGroupByOption.Status: {
          return TASK_ITEM_STATUSES.find((x) => x.id === item.currentStatus)?.name;
        }
        case UserTaskItemsGroupByOption.Team: {
          return item.team?.name ?? "No Team";
        }
        default:
          return;
      }
    },
    [props.userGuid, settings.taskItemsGroupByOption],
  );

  const groupSortByFunction = useCallback(
    (groupName: string) => {
      switch (settings.taskItemsGroupByOption) {
        case UserTaskItemsGroupByOption.DueDate:
          return ["Past Due", "Due Soon", "Due Later", "No Due Date"].indexOf(groupName);
        case UserTaskItemsGroupByOption.InFocus:
          return [groupName !== "Tasks in Focus", groupName];
        case UserTaskItemsGroupByOption.Owner:
          return [groupName !== sessionUserDisplayName, groupName];
        case UserTaskItemsGroupByOption.PrimaryOwner:
          return [groupName !== sessionUserDisplayName, groupName];
        case UserTaskItemsGroupByOption.Status:
          return _.sortBy(TASK_ITEM_STATUSES, (x) => x.sortOrder)
            .map((x) => x.name)
            .indexOf(groupName);
        case UserTaskItemsGroupByOption.Team:
          return [groupName === "No Team", groupName];
        default:
          return groupName;
      }
    },
    [sessionUserDisplayName, settings.taskItemsGroupByOption],
  );

  useEffect(() => {
    if (isLoading) {
      // Clear local state when loading new data
      setGroupedTasks(undefined);
    }
  }, [isLoading]);

  useEffect(() => {
    if (data !== undefined) {
      // Group task items
      const taskItems = _.groupBy(data.taskItems, groupByFunction);

      // Sort task item group names
      const sortedGroupNames = _.sortBy(_.keys(taskItems), groupSortByFunction);

      // Create a map of sorted groups
      const groupedTaskItems = sortedGroupNames.reduce((groupedTaskItems, groupName) => {
        groupedTaskItems.set(groupName, taskItems[groupName]);
        return groupedTaskItems;
      }, new Map());

      if (
        settings.taskItemsGroupByOption === UserTaskItemsGroupByOption.InFocus &&
        taskItems["Tasks in Focus"] === undefined
      ) {
        setGroupedTasks(new Map([["All Tasks", taskItems["All Other Tasks"]]]));
      } else {
        setGroupedTasks(groupedTaskItems);
      }
    }
  }, [data, groupByFunction, groupSortByFunction, settings.taskItemsGroupByOption]);

  function handleOwnershipChanged(taskItemGuid: string, taskItemDescription: string, removedOwners: UserDto[]) {
    if (removedOwners.some((x) => x.userId === sessionUserGuid)) {
      enqueueSnackbar(
        <SnackbarContent
          title="Success"
          content={
            <>
              <span>Your ownership was removed from </span>
              <Link href={Links.TaskItemDetail(taskItemGuid)}>{taskItemDescription}</Link>
            </>
          }
        />,
        {
          variant: "success",
        },
      );

      // Close drawer incase it's open.
      closeDrawer();
    }
  }

  if (isLoading || groupedTasks === undefined) {
    return <AccordionSkeleton isTableView={props.isTableView} />;
  }

  return (
    <Stack spacing={0.5}>
      {groupedTasks.size === 0 ? (
        <Typography variant="placeholder" sx={{ px: 0.5 }}>
          Nothing here yet.
        </Typography>
      ) : (
        Array.from(groupedTasks.keys()).map((groupName, i) => (
          <Accordion
            key={i}
            title={(expanded) => (
              <AccordionTitle
                title={groupName}
                subtitle={expanded ? undefined : `(${groupedTasks.get(groupName)?.length})`}
              />
            )}
            backgroundColor={theme.palette.grey[100]}
            accordionDetailsSx={{ px: props.isTableView ? 0 : 0.5, pb: props.isTableView ? 0 : 0.5 }}
            sx={{
              mb: groupName === sessionUserDisplayName ? "20px !important" : undefined,
            }}
          >
            {props.isTableView ? (
              <TaskItemsTable
                tableId={`userTaskItemsList-${props.userGuid}`}
                taskItems={groupedTasks.get(groupName) ?? []}
                onExpandRowClicked={(row) => row.getToggleExpandedHandler()()}
                onOwnershipChanged={handleOwnershipChanged}
                enableActions={sessionUserGuid === props.userGuid}
              />
            ) : (
              <ViewList
                dataSource={groupedTasks.get(groupName)}
                renderItem={(item) => (
                  <TaskItem
                    key={item.guid}
                    taskItem={item}
                    userCanEdit={item.userCanEdit && sessionUserGuid === props.userGuid}
                    openDrawerOnClick
                    showTeamChip
                    onOwnershipChanged={handleOwnershipChanged}
                    sx={{ px: 0.5 }}
                  />
                )}
              />
            )}
          </Accordion>
        ))
      )}
    </Stack>
  );
};

export { UserTaskItemsList };
